import { useQuery } from '@tanstack/react-query';
import type { FunnelDetailResponse } from '../../lib/api.js';
import { formatNumber } from '../../lib/formatters.js';
import { CHART_COLORS, DIAGNOSIS_CORE_QUESTIONS } from './constants.js';

/**
 * @graph-stack ux-mirror
 * @graph-domain UX
 * @graph-business 診断ファネル専用タブ。diagnosis_lp → diagnosis_result の主要フロー（逆台形）と Q1〜Q20 の質問別到達ユーザー数（横棒）を描画する。コア質問とその他はチャート色で区別する。
 * @graph-connects formatNumber [calls] 数値フォーマット
 */
export function DiagnosisFunnelTab({
  detailQuery,
}: {
  detailQuery: ReturnType<typeof useQuery<FunnelDetailResponse>>;
}) {
  if (detailQuery.isLoading) {
    return (
      <div className="card">
        <div className="skeleton-shimmer h-80 w-full rounded-lg" />
      </div>
    );
  }

  const data = detailQuery.data;
  if (!data || data.steps.length === 0) {
    return (
      <div className="card">
        <p className="text-sm text-[var(--color-text-muted)] text-center py-8">
          データがありません
        </p>
      </div>
    );
  }

  const mainStepNames = ['diagnosis_lp', 'diagnosis_result'];
  const mainSteps = data.steps.filter((s) => mainStepNames.includes(s.stepName));
  const questionSteps = data.steps
    .filter((s) => s.stepName.startsWith('Q'))
    .sort((a, b) => {
      const numA = parseInt(a.stepName.replace('Q', ''), 10);
      const numB = parseInt(b.stepName.replace('Q', ''), 10);
      return numA - numB;
    });

  const maxMainUsers = mainSteps[0]?.uniqueUsers ?? 1;
  const maxQUsers =
    questionSteps.length > 0 ? Math.max(...questionSteps.map((s) => s.uniqueUsers)) : 1;

  return (
    <div className="space-y-6">
      <div className="card animate-fade-in-up">
        <h3 className="text-sm font-semibold text-[var(--color-text-primary)] mb-4">診断フロー</h3>
        <div className="flex flex-col items-center gap-0">
          {mainSteps.map((step, idx) => {
            const widthPct =
              maxMainUsers > 0 ? Math.max((step.uniqueUsers / maxMainUsers) * 100, 15) : 15;
            const color = CHART_COLORS[idx % CHART_COLORS.length];
            const prevUsers = idx > 0 ? mainSteps[idx - 1].uniqueUsers : null;
            const dropPct =
              prevUsers !== null && prevUsers > 0
                ? Math.round(((prevUsers - step.uniqueUsers) / prevUsers) * 1000) / 10
                : null;
            const stepLabel =
              step.stepName === 'diagnosis_lp'
                ? '診断開始'
                : step.stepName === 'diagnosis_result'
                  ? '診断結果'
                  : step.stepName;

            return (
              <div key={step.stepIndex} className="w-full flex flex-col items-center">
                {dropPct !== null && (
                  <div className="flex items-center gap-2 py-0.5">
                    <div className="h-px w-8 bg-[var(--color-border)]" />
                    <span
                      className={`text-[10px] font-medium ${
                        dropPct > 50
                          ? 'text-[var(--color-error)]'
                          : 'text-[var(--color-text-muted)]'
                      }`}
                    >
                      ▼ {String(dropPct)}% 離脱
                    </span>
                    <div className="h-px w-8 bg-[var(--color-border)]" />
                  </div>
                )}
                <div
                  className="relative flex items-center justify-center py-2.5 transition-all duration-300"
                  style={{
                    width: `${String(widthPct)}%`,
                    minWidth: '120px',
                    background: color,
                    opacity: 0.85,
                    clipPath:
                      idx < mainSteps.length - 1
                        ? (() => {
                            const nextWidthPct =
                              maxMainUsers > 0
                                ? Math.max(
                                    (mainSteps[idx + 1].uniqueUsers / maxMainUsers) * 100,
                                    15,
                                  )
                                : 15;
                            const insetPct =
                              widthPct > 0
                                ? Math.max(((widthPct - nextWidthPct) / widthPct) * 50, 0)
                                : 0;
                            return `polygon(0 0, 100% 0, ${String(100 - insetPct)}% 100%, ${String(insetPct)}% 100%)`;
                          })()
                        : 'polygon(0 0, 100% 0, 100% 100%, 0 100%)',
                    borderRadius:
                      idx === 0
                        ? '8px 8px 0 0'
                        : idx === mainSteps.length - 1
                          ? '0 0 8px 8px'
                          : '0',
                  }}
                >
                  <div className="flex items-center gap-2 text-white text-xs font-medium z-10 px-3">
                    <span className="truncate max-w-[140px]">{stepLabel}</span>
                    <span className="font-mono font-bold">{formatNumber(step.uniqueUsers)}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {questionSteps.length > 0 && (
        <div className="card animate-fade-in-up">
          <h3 className="text-sm font-semibold text-[var(--color-text-primary)] mb-1">
            質問別到達ユーザー数
          </h3>
          <p className="text-[10px] text-[var(--color-text-muted)] mb-4">
            質問の表示順はユーザーごとにランダム。
            <span className="inline-block w-2 h-2 rounded-sm bg-[var(--chart-1)] align-middle mx-0.5" />
            コア質問
            <span className="inline-block w-2 h-2 rounded-sm bg-[var(--chart-4)] align-middle mx-0.5 ml-2" />
            条件付き質問
          </p>
          <div className="space-y-1.5">
            {questionSteps.map((step) => {
              const isCore = DIAGNOSIS_CORE_QUESTIONS.has(step.stepName);
              const barWidth =
                maxQUsers > 0 ? Math.max((step.uniqueUsers / maxQUsers) * 100, 2) : 0;

              return (
                <div key={step.stepName} className="flex items-center gap-2">
                  <span className="w-8 text-xs text-[var(--color-text-secondary)] text-right shrink-0 font-mono">
                    {step.stepName}
                  </span>
                  <div className="flex-1 h-5 bg-[var(--color-bg-hover)] rounded overflow-hidden relative">
                    <div
                      className="h-full rounded"
                      style={{
                        width: `${String(barWidth)}%`,
                        background: isCore ? 'var(--chart-1)' : 'var(--chart-4)',
                        opacity: 0.75,
                      }}
                    />
                    <span className="absolute inset-0 flex items-center px-2 text-[10px] font-mono text-[var(--color-text-primary)]">
                      {formatNumber(step.uniqueUsers)}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
